/**
 * Person-import extraction client.
 *
 * WHY THIS EXISTS
 *
 * Up to v100 the import flow read only `file.name` and then served a built-in
 * six-person fixture. Selecting any file produced the same people. That is not
 * production behaviour and has been removed: extraction is now driven by the
 * actual bytes of the file the user chose.
 *
 * WHY IT IS A BACKEND CALL
 *
 * This handoff contains no spreadsheet or PDF parsing capability — no
 * `package.json`, no SheetJS, no pdf.js, nothing that can read XLS/XLSX or PDF
 * in the browser. Rather than fake it, the real `File` is posted to a single
 * extraction endpoint and the server returns the people it found.
 *
 * BACKEND DEPENDENCY: `POST /v1/person-import/extract` DOES NOT EXIST YET.
 * The contract is documented in API_CONTRACT.md. Until it ships, Import shows
 * an extraction error — it does not fall back to sample data, because silently
 * substituting fixture rows for a customer's real roster is exactly the defect
 * this module was written to remove.
 *
 * SCOPE: extraction only. Creating people still goes through the existing
 * `POST /seniors` with the unchanged canonical person payload.
 */
import axios from "axios";
import { normalizeExternalId } from "../../utlis/externalId";
import { APP_URL } from "../../services/serviceConstants";
import { getActiveLocale } from "../../configs/regionalProfile";

const api = axios.create({ baseURL: APP_URL });

export const EXTRACT_ENDPOINT = "/v1/person-import/extract";
export const ACCEPTED_FILE_TYPES = ".pdf,.xls,.xlsx";

/**
 * Send the real file for extraction.
 *
 * multipart/form-data:
 *   file          the File the user selected, bytes intact
 *   customerType  home_care | senior_living | rehab | primary_care
 *
 * The browser sets the multipart boundary, so Content-Type is deliberately not
 * set by hand.
 */
export async function extractPeopleFromFile(session, file, customerType) {
  if (!file) throw new Error("No file supplied");

  const body = new FormData();
  body.append("file", file, file.name);
  body.append("customerType", customerType || "");

  const response = await api.post(EXTRACT_ENDPOINT, body, {
    headers: {
      Authorization: `Bearer ${session.accessToken}`,
      "Accept-Language": getActiveLocale(),
      Accept: "application/json",
    },
  });

  return normalizeExtractionResponse(response.data);
}

/**
 * Server response -> the row shape the Review screen already understands.
 *
 * `source` is whatever the document said, kept verbatim for Source Details.
 * `normalized` carries canonical cm / kg. A field the server could not read
 * comes back empty and is reported by the existing registration-gate rules, so
 * a partial extraction surfaces as Needs Attention rather than as a silent gap.
 */
export function normalizeExtractionResponse(payload) {
  const people = payload?.data;
  if (!Array.isArray(people)) {
    throw new Error("Extraction response did not contain a people array");
  }
  return people.map((person, index) => {
    const n = person?.normalized || {};
    return {
      _id: `x${index}`,
      // The extraction contract returns the customer's external ID under the
      // canonical person-payload key `user_id_from_client` (see the /extract
      // response and toPatientPayload). Fall back to older names for resilience.
      extId: normalizeExternalId(
        n.user_id_from_client ?? n.external_id ?? n.test_person_id,
      ),
      firstName: n.first_name ?? "",
      lastName: n.last_name ?? "",
      gender: n.gender ?? "",
      birthYear: n.birth_year ?? "",
      birthMonth: n.birth_month ?? "",
      heightCm:
        n.height_cm === null || n.height_cm === undefined
          ? ""
          : Number(n.height_cm),
      weightKg:
        n.weight_kg === null || n.weight_kg === undefined
          ? ""
          : Number(n.weight_kg),
      source: person?.source || {},
    };
  });
}
